/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Form, DateInput, Tooltip } from '@mairistem/composants';
import { Section } from '@mairistem/administres-utils';

import { DATE_FORMAT, URL_AUTORITES_RECONNAISSANCE } from '@/constants';
import { useOptionsReferentiel } from '@/hooks';
import { createTimeInputHandler } from '@/utils';

import { ChampLieu } from '@/components/Common';

interface InformationsEvenementReconnaissanceProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (
    event: React.SyntheticEvent | null,
    payload: { name: string; value: unknown },
  ) => void;
}

/**
 * Bloc « INFORMATIONS SUR L'EVENEMENT » de l'acte de reconnaissance : date et
 * heure de la reconnaissance, auteur (`ec_autrec` : Le père seul / La mère
 * seule / Le père et la mère), lieu de reconnaissance.
 *
 * Construit avec les mêmes primitives que `InformationsActe` (`Section`,
 * `Form`, `DateInput` + `Tooltip`, `Form.Dropdown`, `createTimeInputHandler`)
 * pour rester visuellement cohérent, sans dupliquer aucun champ de
 * `InformationsActe`/`InformationsEnfant`.
 */
const InformationsEvenementReconnaissance = (
  props: InformationsEvenementReconnaissanceProps,
) => {
  const { data, errors = {}, onChange } = props;

  const { options: auteurOptions, items: autoritesReconnaissance } =
    useOptionsReferentiel(URL_AUTORITES_RECONNAISSANCE);

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateReconnaissance',
        value: _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
      });
    },
    [onChange],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(onChange),
    [onChange],
  );

  /**
   * Comme `InformationsActe.handleSousTypeChange` : le back attend
   * l'`identifiant` (`au_auto`) dans `auteurReconnaissance`, mais la
   * ventilation date/lieu (père/mère/les deux) est faite côté back à partir du
   * CODE (`au_codj`, `'P'|'M'|'PM'`). On stocke le code dérivé pour permettre
   * d'éventuelles règles front ultérieures, même si l'API assure la
   * ventilation réelle.
   */
  const handleAuteurChange = React.useCallback(
    (
      e: React.SyntheticEvent | null,
      { name, value }: { name: string; value: unknown },
    ) => {
      onChange(e, { name, value });
      const item = _.find(autoritesReconnaissance, { identifiant: value });
      onChange(null, {
        name: 'codeAuteurReconnaissance',
        value: item?.codeJuridique ?? null,
      });
    },
    [onChange, autoritesReconnaissance],
  );

  const dateValiditeLieu = !_.isEmpty(data?.dateReconnaissance)
    ? dayjs(data.dateReconnaissance, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section title="INFORMATIONS SUR L'EVENEMENT" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: '1em',
          }}
        >
          <Form.Field error={!_.isNil(errors?.dateReconnaissance)}>
            <label>Reconnu le</label>
            <Tooltip
              trigger={
                <DateInput
                  value={
                    data?.dateReconnaissance
                      ? dayjs(data.dateReconnaissance, DATE_FORMAT).toDate()
                      : undefined
                  }
                  onChange={handleDateChange}
                />
              }
              type={errors?.dateReconnaissance ? 'error' : ''}
              message={errors?.dateReconnaissance}
            />
          </Form.Field>
          <Form.Group style={{ alignItems: 'flex-end' }}>
            <Tooltip
              trigger={
                <Form.Input
                  label="à"
                  placeholder="hh"
                  name="heureReconnaissance"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.heureReconnaissance)}
                  value={
                    !_.isNil(data?.heureReconnaissance)
                      ? String(data.heureReconnaissance).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.heureReconnaissance ? 'error' : ''}
              message={errors?.heureReconnaissance}
            />
            <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>:</span>
            <Tooltip
              trigger={
                <Form.Input
                  placeholder="mm"
                  name="minuteReconnaissance"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.minuteReconnaissance)}
                  value={
                    !_.isNil(data?.minuteReconnaissance)
                      ? String(data.minuteReconnaissance).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.minuteReconnaissance ? 'error' : ''}
              message={errors?.minuteReconnaissance}
            />
          </Form.Group>
          <Form.Dropdown
            width={6}
            selection
            required
            name="auteurReconnaissance"
            label="Auteur de la reconnaissance"
            placeholder="Choisir un auteur"
            options={auteurOptions}
            value={data?.auteurReconnaissance ?? ''}
            error={!_.isNil(errors?.auteurReconnaissance)}
            onChange={handleAuteurChange}
          />
        </div>
        <ChampLieu
          data={data}
          errors={errors}
          onChange={onChange}
          name="adresseReconnaissance"
          title="Lieu de reconnaissance"
          dateValidite={dateValiditeLieu}
        />
      </Form>
    </Section>
  );
};

export default InformationsEvenementReconnaissance;
